import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Moment from 'moment';
import { toast } from '@rickylandino/react-messages';
import NumberFormat from 'react-number-format';

import { getAllContactTypes, createTrackingAction, updateTrackingAction, downloadAttachment } from '../../common/services/next-gen/PatientTrackingService';
import Globals from '../../config/globals';

import { getAssociatedUsers } from '../../common/services/next-gen/UserService';

/**
 * AddPatientTrackingActionModal Component
 * 
 * Modal dialog for adding a new patient tracking action.
 * Handles contact type selection, contacted by user selection,
 * downstream facility information, notes, and follow-up date calculation.
 * 
 * @component
 * @example
 * <AddPatientTrackingActionModal
 *   show={true}
 *   onHide={() => setShowModal(false)}
 *   patientResultId="guid-here"
 *   currentFollowUpDate="2025-09-24"
 *   onActionAdded={(newAction) => console.log(newAction)}
 * />
 */
const AddPatientTrackingActionModal = ({ show, onHide, patientExamId, patientResultId, currentFollowUpDate, onActionAdded, existingAction, pendingNotesFlush }) => {
    const isEditMode = !!existingAction;

    const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
    const theme = window.sessionStorage.getItem("theme") || 'light';

    const [contactTypes, setContactTypes] = useState([]);
    const [loadingContactTypes, setLoadingContactTypes] = useState(true);

    // Form fields
    const [contactTypeId, setContactTypeId] = useState('');
    const [contactedByUserId, setContactedByUserId] = useState(userInfo?.userId || '');
    const [contactedByName, setContactedByName] = useState(userInfo?.name || '');
    const [facilityName, setFacilityName] = useState('');
    const [facilityPointOfContact, setFacilityPointOfContact] = useState('');
    const [facilityPhone, setFacilityPhone] = useState('');
    const [facilityFax, setFacilityFax] = useState('');
    const [notes, setNotes] = useState('');
    // Initialize both old and new dates to the same value (current follow-up date)
    const initialFollowUpDate = currentFollowUpDate ? Moment(currentFollowUpDate).toDate() : null;
    const [nextFollowUpDate, setNextFollowUpDate] = useState(initialFollowUpDate);
    const [oldFollowUpDate, setOldFollowUpDate] = useState(initialFollowUpDate);
    const [contactDate, setContactDate] = useState(Moment().toDate());

    // Scheduled appointment fields
    const [showAppointmentDateField, setShowAppointmentDateField] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState(null);

    // Non-compliant fields
    const [showNonCompliantFields, setShowNonCompliantFields] = useState(false);
    const [nonCompliantTrackingNumber, setNonCompliantTrackingNumber] = useState('');
    const [nonCompliantAttachment, setNonCompliantAttachment] = useState(null);
    const [nonCompliantAttachmentFileName, setNonCompliantAttachmentFileName] = useState('');
    const [hasExistingAttachment, setHasExistingAttachment] = useState(false);

    // State
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // User list for "Contacted By" dropdown
    const [availableUsers, setAvailableUsers] = useState([]);

    /**
     * Load contact types and available users on mount
     */
    useEffect(() => {
        loadContactTypes();
        loadAvailableUsers();
    }, []);

    /**
     * Reset form when modal opens
     */
    useEffect(() => {
        if (show) {
            if (existingAction) {
                // Pre-fill form with existing action data
                setContactTypeId(existingAction.contactTypeId || '');
                setContactedByUserId(existingAction.contactedByUserId || '');
                setFacilityName(existingAction.facilityName || '');
                setFacilityPointOfContact(existingAction.facilityPointOfContact || '');
                setFacilityPhone(existingAction.facilityPhone || '');
                setFacilityFax(existingAction.facilityFax || '');
                setNotes(existingAction.notes || '');
                setNextFollowUpDate(existingAction.nextFollowUpDate ? Moment(existingAction.nextFollowUpDate).toDate() : null);
                setOldFollowUpDate(currentFollowUpDate ? Moment(currentFollowUpDate).toDate() : null);
                setContactDate(existingAction.contactDate ? Moment(existingAction.contactDate).toDate() : Moment().toDate());

                // Check if this contact type requires non-compliant fields
                const contactType = contactTypes.find(ct => ct.contactTypeId === existingAction.contactTypeId);
                setShowNonCompliantFields(contactType?.requiresNonCompliant || false);

                // Pre-fill non-compliant fields if present
                if (existingAction.nonCompliantTrackingNumber) {
                    setNonCompliantTrackingNumber(existingAction.nonCompliantTrackingNumber);
                }
                if (existingAction.nonCompliantAttachmentFileName) {
                    setNonCompliantAttachmentFileName(existingAction.nonCompliantAttachmentFileName);
                    setHasExistingAttachment(true);
                    // Note: We can't restore the file, but we can show the filename
                }

                // Restore appointment date for Scheduled Appointment contact type
                // Driven by the RequiresAppointmentDate column, not the contact type name - the old
        // string match broke silently whenever the row was renamed.
        const isScheduledAppointment = contactType?.requiresAppointmentDate === true;
                setShowAppointmentDateField(isScheduledAppointment);
                if (isScheduledAppointment && existingAction.appointmentDate) {
                    setAppointmentDate(Moment(existingAction.appointmentDate).toDate());
                }
            } else {
                resetForm();
            }
        }
    }, [show, existingAction, contactTypes]);

    /**
     * Load all contact types from the API
     */
    const loadContactTypes = async () => {
        try {
            const types = await getAllContactTypes();
            setContactTypes(types);
            setLoadingContactTypes(false);
        } catch (error) {
            console.error('Error loading contact types:', error);
            toast.error('Failed to load contact types');
            setLoadingContactTypes(false);
        }
    };

    /**
     * Load available users from facility
     * This includes users with access to the patient's facility plus a "Practice" option
     */
    const loadAvailableUsers = async () => {
        var facUsers = await getAssociatedUsers();

        // Add current user if not in list
        if (userInfo && !facUsers.find(u => u.userID === userInfo.userId)) {
            facUsers.unshift({
                userId: userInfo.userId,
                name: userInfo.fullName
            });
        }

        // Add "Practice" as an option
        const users = [
            { userId: 'PRACTICE', name: 'Practice' },
            ...facUsers
        ];

        setAvailableUsers(users);
    };

    /**
     * Reset form to initial state
     */
    const resetForm = () => {
        setContactTypeId('');
        setContactedByUserId(userInfo?.userId || '');
        setContactedByName(userInfo?.name || '');
        setFacilityName('');
        setFacilityPointOfContact('');
        setFacilityPhone('');
        setFacilityFax('');
        setNotes('');
        
        // Set both old and new dates to the current follow-up date
        const followUpDateValue = currentFollowUpDate ? Moment(currentFollowUpDate).toDate() : null;
        setOldFollowUpDate(followUpDateValue);
        setNextFollowUpDate(followUpDateValue); // Default new date to match old date
        
        setShowNonCompliantFields(false);
        setNonCompliantTrackingNumber('');
        setNonCompliantAttachment(null);
        setNonCompliantAttachmentFileName('');
        setHasExistingAttachment(false);
        setShowAppointmentDateField(false);
        setAppointmentDate(null);
        setContactDate(Moment().toDate());
        setErrors({});
    };

    /**
     * Handle contact type selection
     * Updates follow-up date based on daysUntilFollowUp setting
     * Shows non-compliant fields if required
     */
    const handleContactTypeChange = (e) => {
        const typeId = e.target.value;
        const contactType = contactTypes.find(ct => ct.contactTypeId === typeId);

        // Driven by the RequiresAppointmentDate column, not the contact type name - the old
        // string match broke silently whenever the row was renamed.
        const isScheduledAppointment = contactType?.requiresAppointmentDate === true;
        setShowAppointmentDateField(isScheduledAppointment);

        // Clear appt date when switching away from Scheduled Appointment
        if (!isScheduledAppointment) {
            setAppointmentDate(null);
        }

        let newFollowUpDate = oldFollowUpDate;

        if (!isScheduledAppointment && contactType && contactType.daysUntilFollowUp !== null) {
            // Calculate new follow-up date based on Contact Date + daysUntilFollowUp
            const baseDate = contactDate || Moment();
            newFollowUpDate = Moment(baseDate).add(contactType.daysUntilFollowUp, 'days').toDate();
        }

        setContactTypeId(typeId);
        setNextFollowUpDate(newFollowUpDate);
        setShowNonCompliantFields(contactType?.requiresNonCompliant || false);
        setErrors({ ...errors, contactTypeId: null });
    };

    /**
     * Handle contact date change
     * Recalculates follow-up date if a contact type is already selected
     */
    const handleContactDateChange = (date) => {
        setContactDate(date);
        if (contactTypeId) {
            const contactType = contactTypes.find(ct => ct.contactTypeId === contactTypeId);
            if (contactType && contactType.daysUntilFollowUp !== null) {
                setNextFollowUpDate(Moment(date).add(contactType.daysUntilFollowUp, 'days').toDate());
            }
        }
    };

    /**
     * Handle appointment date change (Scheduled Appointment contact type only)
     * Sets the new tracking date to 7 days after the appointment date
     */
    const handleAppointmentDateChange = (date) => {
        setAppointmentDate(date);
        if (date) {
            setNextFollowUpDate(Moment(date).add(7, 'days').toDate());
        }
    };

    /**
     * Handle contacted by selection
     */
    const handleContactedByChange = (e) => {
        const userId = e.target.value;
        const user = availableUsers.find(u => u.userId === userId);

        setContactedByUserId(userId === 'PRACTICE' ? null : userId);
        setContactedByName(user?.name || '');
    };

    /**
     * Handle file attachment
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        
        if (file) {
            // Convert to base64
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64 = event.target.result.split(',')[1]; // Remove data:*/*;base64, prefix
                setNonCompliantAttachment(base64);
                setNonCompliantAttachmentFileName(file.name);
                setHasExistingAttachment(false); // New file selected
                setErrors({ ...errors, nonCompliantAttachment: null });
            };
            reader.readAsDataURL(file);
        }
    };

    /**
     * Handle downloading existing attachment
     */
    const handleDownloadAttachment = async () => {
        try {
            const actionId = existingAction.trackingActionId 
                || existingAction.id 
                || existingAction.patientTrackingActionId 
                || existingAction.actionId;
            
            const response = await downloadAttachment(actionId);
            
            // Create blob and download
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from response headers or use stored filename
            const contentDisposition = response.headers['content-disposition'];
            let filename = nonCompliantAttachmentFileName || 'attachment';
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading attachment:', error);
            alert('Failed to download attachment');
        }
    };

    /**
     * Validate form fields
     */
    const validate = () => {
        const newErrors = {};

        if (!contactTypeId) {
            newErrors.contactTypeId = 'Contact type is required';
        }

        if (showNonCompliantFields) {
            if (!nonCompliantTrackingNumber) {
                newErrors.nonCompliantTrackingNumber = 'Tracking number is required for non-compliant contacts';
            }
            // Only require attachment if no existing attachment and no new one selected
            if (!hasExistingAttachment && !nonCompliantAttachment) {
                newErrors.nonCompliantAttachment = 'Attachment is required for non-compliant contacts';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async () => {
        if (!validate()) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSubmitting(true);

        try {
            const data = {
                contactTypeId,
                contactedByUserId: contactedByUserId || null,
                facilityName: facilityName || null,
                facilityPointOfContact: facilityPointOfContact || null,
                facilityPhone: facilityPhone || null,
                facilityFax: facilityFax || null,
                notes: notes || null,
                contactDate: contactDate ? Moment(contactDate).format('YYYY-MM-DD') : Moment().format('YYYY-MM-DD'),
                nextFollowUpDate: nextFollowUpDate
                    ? Moment(nextFollowUpDate).format('YYYY-MM-DD')
                    : null,
                appointmentDate: showAppointmentDateField && appointmentDate
                    ? Moment(appointmentDate).format('YYYY-MM-DD')
                    : null
            };

            // Add patientExamId and patientResultId only for create (not update)
            if (!isEditMode) {
                data.patientExamId = patientExamId;
                data.patientResultId = patientResultId || null;
            }

            // Add non-compliant fields if required
            if (showNonCompliantFields) {
                data.nonCompliantTrackingNumber = nonCompliantTrackingNumber;
                // Only include attachment if a new one was selected
                if (nonCompliantAttachment) {
                    data.nonCompliantAttachment = nonCompliantAttachment;
                    data.nonCompliantAttachmentFileName = nonCompliantAttachmentFileName;
                }
            }

            // The result's notes box is flushed when this modal opens. Wait for it before either
            // write: the server rebuilds that result's notes from what is in the database on both
            // create and update, so an action written first would discard free text the flush is
            // still saving. The flush reports its own failure and the action is written either way.
            if (pendingNotesFlush?.current) {
                await pendingNotesFlush.current;
                pendingNotesFlush.current = null;
            }

            let resultAction;
            if (isEditMode) {
                // Get the tracking action ID from the existing action
                // Try multiple possible property names from the API
                const actionId = existingAction.trackingActionId
                    || existingAction.id
                    || existingAction.patientTrackingActionId
                    || existingAction.actionId;

                if (!actionId) {
                    toast.error('Cannot update: Missing tracking action ID');
                    setSubmitting(false);
                    return;
                }

                resultAction = await updateTrackingAction(actionId, data);
                toast.success('Patient tracking action updated successfully');
            } else {
                resultAction = await createTrackingAction(data);
                toast.success('Patient tracking action added successfully');
            }

            // Call parent callback
            if (onActionAdded) {
                onActionAdded(resultAction, isEditMode);
            }

            // Close modal
            onHide();

        } catch (error) {
            if (error?.response?.status === 400) {
                toast.error('Only the most recent tracking action can be edited.');
            } else {
                toast.error('Failed to save patient tracking action');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            size='lg'
            show={show}
            onHide={onHide}
            backdrop='static'
            autoFocus={false}
            className="modal-popup"
            dialogClassName={theme === 'dark' ? 'dark-theme' : 'light-theme'}
        >
            <Modal.Header>
                <Modal.Title>{isEditMode ? 'Edit Follow Up Tracking Action' : 'Add Follow Up Tracking Action'}</Modal.Title>
                <span
                    className="a hover alignRight close"
                    onClick={onHide}
                    style={{ cursor: 'pointer' }}
                >
                    &times;
                </span>
            </Modal.Header>

            <Modal.Body>
                <div className="panel-content">
                    {/* Contact Date */}
                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            <strong>Contact Date:</strong>
                        </label>
                        <div className="col-md-3">
                            <DatePicker
                                selected={contactDate}
                                onChange={handleContactDateChange}
                                dateFormat="MM/dd/yyyy"
                                className="form-control"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                customInput={<NumberFormat format="##/##/####" mask="_" />}
                            />
                        </div>
                    </div>

                    {/* Contact Type */}
                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            <strong>Contact Type <span className="color-pink">*</span></strong>
                        </label>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                value={contactTypeId}
                                onChange={handleContactTypeChange}
                                disabled={loadingContactTypes}
                            >
                                <option value="">-- Select Contact Type --</option>
                                {contactTypes.filter(ct => !ct.isSystemGenerated).map(ct => (
                                    <option key={ct.contactTypeId} value={ct.contactTypeId}>
                                        {ct.contactTypeName}
                                    </option>
                                ))}
                            </select>
                            {errors.contactTypeId && (
                                <small className="text-danger">{errors.contactTypeId}</small>
                            )}
                        </div>
                    </div>

                    {/* Contacted By */}
                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            <strong>Contacted By:</strong>
                        </label>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                value={contactedByUserId || 'PRACTICE'}
                                onChange={handleContactedByChange}
                            >
                                {availableUsers.map(user => (
                                    <option key={user.userId} value={user.userId}>
                                        {user.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            <strong>Notes:</strong>
                        </label>
                        <div className="col-md-9">
                            <textarea
                                className="form-control"
                                rows="4"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Enter notes about this contact"
                            />
                        </div>
                    </div>

                    {/* Outside Facility Fields */}
                    <div className="form-group row">
                        <div className="col-12">
                            <h6><strong>Outside Facility Information</strong></h6>
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            Facility Name:
                        </label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                className="form-control"
                                value={facilityName}
                                onChange={(e) => setFacilityName(e.target.value)}
                                placeholder="Enter facility name"
                                maxLength={200}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            Point of Contact:
                        </label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                className="form-control"
                                value={facilityPointOfContact}
                                onChange={(e) => setFacilityPointOfContact(e.target.value)}
                                placeholder="Enter contact person name"
                                maxLength={200}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            Phone:
                        </label>
                        <div className="col-md-4">
                            <NumberFormat
                                className="form-control"
                                value={facilityPhone}
                                onValueChange={(values) => setFacilityPhone(values.formattedValue)}
                                format="(###) ###-####"
                                mask="_"
                                placeholder="(555) 123-4567"
                            />
                        </div>
                        <label className="col-md-1 col-form-label">
                            Fax:
                        </label>
                        <div className="col-md-4">
                            <NumberFormat
                                className="form-control"
                                value={facilityFax}
                                onValueChange={(values) => setFacilityFax(values.formattedValue)}
                                format="(###) ###-####"
                                mask="_"
                                placeholder="(555) 123-4568"
                            />
                        </div>
                    </div>

                    {/* Appointment Date (Scheduled Appointment contact type only) */}
                    {showAppointmentDateField && (
                        <div className="form-group row">
                            <label className="col-md-3 col-form-label">
                                <strong>Appointment Date:</strong>
                            </label>
                            <div className="col-md-3">
                                <DatePicker
                                    selected={appointmentDate}
                                    onChange={handleAppointmentDateChange}
                                    dateFormat="MM/dd/yyyy"
                                    className="form-control"
                                    placeholderText="Select appt date"
                                    minDate={contactDate ? Moment(contactDate).toDate() : null}
                                    popperPlacement="top-start"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                                </div>
                            <div className="col-md-6 col-form-label">
                                <small className="text-muted">
                                    New tracking date will default to 7 days after the appointment date.
                                </small>
                            </div>
                        </div>
                    )}

                    {/* Follow Up Dates */}
                    <div className="form-group row">
                        <label className="col-md-3 col-form-label">
                            Old Patient Tracking Date:
                        </label>
                        <div className="col-md-3">
                            <input
                                type="text"
                                className="form-control"
                                value={oldFollowUpDate ? Moment(oldFollowUpDate).format('MM/DD/YYYY') : 'N/A'}
                                readOnly
                                disabled
                            />
                        </div>
                        <label className="col-md-3 col-form-label">
                            New Patient Tracking Date:
                        </label>
                        <div className="col-md-3">
                            <DatePicker
                                selected={nextFollowUpDate}
                                onChange={(date) => setNextFollowUpDate(date)}
                                dateFormat="MM/dd/yyyy"
                                className="form-control"
                                placeholderText="Select date"
                                popperClassName="patient-tracking-datepicker-popper"
                                popperPlacement="top-end"
                                minDate={contactDate ? Moment(contactDate).toDate() : null}
                                maxDate={
                                    showAppointmentDateField && appointmentDate
                                        ? Moment(appointmentDate).add(7, 'days').toDate()
                                        : contactDate
                                            ? Moment(contactDate).add(
                                                contactTypes.find(ct => ct.contactTypeId === contactTypeId)?.daysUntilFollowUp ?? 7,
                                                'days'
                                              ).toDate()
                                            : null
                                }
                            />
                        </div>
                    </div>

                    {/* Non-Compliant Fields */}
                    {showNonCompliantFields && (
                        <>
                            <div className="form-group row mt-4">
                                <div className="col-12">
                                    <h6 className="text-danger">
                                        <strong>Non-Compliant Information Required</strong>
                                    </h6>
                                </div>
                            </div>

                            <div className="form-group row">
                                <label className="col-md-3 col-form-label">
                                    <strong>Tracking Number <span className="color-pink">*</span></strong>
                                </label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={nonCompliantTrackingNumber}
                                        onChange={(e) => setNonCompliantTrackingNumber(e.target.value)}
                                        placeholder="Enter tracking number"
                                        maxLength={100}
                                    />
                                    {errors.nonCompliantTrackingNumber && (
                                        <small className="text-danger">{errors.nonCompliantTrackingNumber}</small>
                                    )}
                                </div>
                            </div>

                            <div className="form-group row">
                                <label className="col-md-3 col-form-label">
                                    <strong>Attachment <span className="color-pink">*</span></strong>
                                </label>
                                <div className="col-md-9">
                                    {hasExistingAttachment && (
                                        <div className="mb-2">
                                            <i 
                                                className="fas fa-paperclip" 
                                                style={{ color: '#f30079', marginRight: '8px' }}
                                            />
                                            <span 
                                                className="text-primary" 
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                onClick={handleDownloadAttachment}
                                            >
                                                {nonCompliantAttachmentFileName}
                                            </span>
                                            <small className="text-muted d-block">
                                                Upload a new file to replace this attachment
                                            </small>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        className="form-control-file"
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    />
                                    {!hasExistingAttachment && nonCompliantAttachmentFileName && (
                                        <small className="text-muted">
                                            Selected: {nonCompliantAttachmentFileName}
                                        </small>
                                    )}
                                    {errors.nonCompliantAttachment && (
                                        <small className="text-danger d-block">{errors.nonCompliantAttachment}</small>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button
                    type="button"
                    onClick={onHide}
                    className="btn btn-outline-default"
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-submit"
                    disabled={submitting}
                >
                    {submitting ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddPatientTrackingActionModal;
